import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import ListSection from "@/components/ListSection";
import api, { formatApiError, downloadReferenceDimensionsPdf, downloadReferenceAttachment } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Send, Copy, CheckCircle2, Clock, Loader2, Star, ThumbsUp, ThumbsDown, Quote, BellRing, LayoutList, Download, Paperclip } from "lucide-react";

const RELATIONS = ["Vän", "Familj", "Chef", "Kollega", "Lärare", "Handledare", "Annat"];
const TYPES = ["Karaktärsreferens", "Yrkesreferens"];

// Etiketter för alla möjliga referensfrågor (arbets- och sociala frågor delar D-id:n där det passar).
const DIM_LABELS = {
  D1_RESPONSIBILITY: "Ansvar och pålitlighet",
  D2_COLLABORATION: "Samarbete och bemötande",
  D3_STRUCTURE: "Struktur och kvalitet",
  D4_INITIATIVE: "Initiativ och problemlösning",
  D5_ADAPTABILITY: "Anpassning och press",
  D6_LEARNING: "Lärande och återkoppling",
  D7_MOTIVATION: "Motivation och arbetsmiljö",
  D8_SOCIAL_ROLE: "Social stil och grupproll",
  D9_INTERESTS: "Intressen och hjärtefrågor",
  D10_DEVELOPMENT: "Utveckling och framtid",
  S1_SOCIAL: "I sociala sammanhang",
  S2_TRUST: "Pålitlighet och att hålla ord",
  S3_EMPATHY: "Omtanke och empati",
  S4_DRIVE: "Engagemang och hjärtefrågor",
  S5_GROUP: "Roll i grupp och umgänge",
  S6_VALUES: "Personliga egenskaper och värderingar",
};

const FIELDS = [
  { name: "name", label: "Namn", type: "text", placeholder: "Referensens namn" },
  { name: "email", label: "E-post", type: "text", placeholder: "namn@exempel.se" },
  { name: "relation", label: "Relation", type: "select", options: RELATIONS },
  { name: "ref_type", label: "Typ av referens", type: "select", options: TYPES },
];

const STATUS = {
  sent: { label: "Inbjudan skickad", icon: Send, cls: "text-primary" },
  link_ready: { label: "Länk klar att dela", icon: Clock, cls: "text-[hsl(var(--warning))]" },
  received: { label: "Svar inkommet", icon: CheckCircle2, cls: "text-[hsl(var(--success))]" },
};

function ResponseView({ resp }) {
  return (
    <div className="mt-3 rounded-xl border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 p-4" data-testid={`response-${resp.reference_id}`}>
      <div className="flex items-center gap-3 mb-2">
        {resp.rating ? (
          <span className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className="h-4 w-4 text-[hsl(var(--warning))]" fill={n <= resp.rating ? "currentColor" : "none"} />
            ))}
          </span>
        ) : null}
        {resp.would_recommend === true && <Badge variant="secondary" className="rounded-full text-[hsl(var(--success))]"><ThumbsUp className="h-3 w-3 mr-1" /> Rekommenderar</Badge>}
        {resp.would_recommend === false && <Badge variant="secondary" className="rounded-full text-muted-foreground"><ThumbsDown className="h-3 w-3 mr-1" /> Rekommenderar ej</Badge>}
      </div>
      <p className="text-sm text-foreground/90 flex gap-2"><Quote className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />{resp.message}</p>
      {resp.dimensions && Object.keys(resp.dimensions).length > 0 && (
        <div className="mt-3 space-y-2 border-t border-[hsl(var(--success))]/20 pt-3" data-testid={`response-dimensions-${resp.reference_id}`}>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bekräftade egenskaper</div>
          {Object.entries(resp.dimensions).map(([id, d]) => (
            <div key={id} className="text-sm">
              <span className="font-medium">{DIM_LABELS[id] || id}:</span>{" "}
              {d.cannot_assess ? <span className="text-muted-foreground italic">Kunde ej bedöma</span> : <span className="text-foreground/90">{d.text}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InviteAction({ item, reload, response, attachments }) {
  const [busy, setBusy] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [link, setLink] = useState(item.token ? `${window.location.origin}/referens/${item.token}` : null);
  const st = STATUS[item.status];
  const invited = item.status === "sent" || item.status === "link_ready";

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/references/${item.id}/invite`);
      setLink(data.link);
      if (data.emailed) toast.success("Inbjudan skickad via e-post");
      else toast.success("Länk skapad – dela den med din referens");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remind = async () => {
    setReminding(true);
    try {
      const { data } = await api.post(`/references/${item.id}/remind`);
      if (data.emailed) toast.success("Påminnelse skickad via e-post");
      else toast.success("Ingen e-post konfigurerad – dela länken igen som påminnelse");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setReminding(false); }
  };

  const copy = (l) => { navigator.clipboard.writeText(l); toast.success("Länk kopierad"); };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {st && (
          <Badge variant="secondary" className={`rounded-full ${st.cls}`}>
            <st.icon className="h-3 w-3 mr-1" /> {st.label}
          </Badge>
        )}
        {item.status !== "received" && (
          <Button data-testid={`invite-${item.id}`} size="sm" variant="outline" className="rounded-full" onClick={invite} disabled={busy}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
            {item.status ? "Skicka igen" : "Skicka inbjudan"}
          </Button>
        )}
        {invited && (
          <Button data-testid={`remind-${item.id}`} size="sm" variant="ghost" className="rounded-full text-primary" onClick={remind} disabled={reminding}>
            {reminding ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <BellRing className="h-3.5 w-3.5 mr-1" />}
            Skicka påminnelse
          </Button>
        )}
        {link && item.status !== "received" && (
          <Button size="sm" variant="ghost" className="rounded-full" onClick={() => copy(link)}>
            <Copy className="h-3.5 w-3.5 mr-1" /> Kopiera länk
          </Button>
        )}
      </div>
      {item.reminded_at && item.status !== "received" && (
        <p className="text-xs text-muted-foreground mt-1.5">Påminnelse skickad {new Date(item.reminded_at).toLocaleDateString("sv-SE")}{item.reminder_count > 1 ? ` (${item.reminder_count} ggr)` : ""}</p>
      )}
      {response && <ResponseView resp={response} />}
      {(attachments || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`attachments-${item.id}`}>
          {attachments.map((f) => (
            <Button key={f.id} size="sm" variant="outline" className="rounded-full"
              onClick={async () => { try { await downloadReferenceAttachment(f.id, f.original_filename); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}
              data-testid={`attachment-download-${f.id}`}>
              <Paperclip className="h-3.5 w-3.5 mr-1.5" /> {f.original_filename}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Referenser() {
  const [responses, setResponses] = useState({});
  const [refList, setRefList] = useState([]);
  const [attByRef, setAttByRef] = useState({});

  const loadResponses = () => {
    api.get("/references/responses").then((r) => {
      const map = {};
      (r.data || []).forEach((resp) => { map[resp.reference_id] = resp; });
      setResponses(map);
    });
    api.get("/references").then((r) => setRefList(r.data || [])).catch(() => {});
    api.get("/references/attachments").then((r) => {
      const map = {};
      (r.data || []).forEach((a) => { (map[a.reference_id] = map[a.reference_id] || []).push(a); });
      setAttByRef(map);
    }).catch(() => {});
  };
  useEffect(() => { loadResponses(); }, []);

  const nameOf = (refId) => (refList.find((x) => x.id === refId) || {}).name || "Referens";
  const presentIds = [];
  Object.values(responses).forEach((resp) => {
    Object.keys(resp.dimensions || {}).forEach((id) => { if (!presentIds.includes(id)) presentIds.push(id); });
  });
  const dimGroups = presentIds.map((id) => {
    const answers = [];
    Object.values(responses).forEach((resp) => {
      const a = (resp.dimensions || {})[id];
      if (a && (a.cannot_assess || (a.text || "").trim())) {
        answers.push({ name: nameOf(resp.reference_id), text: a.text, cannot_assess: a.cannot_assess });
      }
    });
    return { value: id, label: DIM_LABELS[id] || id, answers };
  }).filter((g) => g.answers.length > 0);

  return (
    <div>
      <PageHeader
        title="Referenser"
        description="Lägg till personer som kan gå i god för dig och skicka en referensinbjudan. Frågorna anpassas automatiskt: en karaktärsreferens som är vän eller familj får frågor om hur du är som person och i sociala sammanhang, medan yrkesreferenser (och karaktärsreferenser som är chef eller kollega) får frågor om arbetslivet. Inkomna svar visas direkt här nedanför."
      />

      {dimGroups.length > 0 && (
        <Card className="rounded-2xl border-border p-6 mb-6" data-testid="dimension-overview">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
            <div className="flex items-center gap-2"><LayoutList className="h-5 w-5 text-primary" /><h3 className="font-head text-lg font-semibold">Styrkor bekräftade av dina referenser</h3></div>
            <Button variant="outline" size="sm" className="rounded-full shrink-0" data-testid="download-ref-pdf-btn"
              onClick={async () => { try { await downloadReferenceDimensionsPdf(); toast.success("Sammanställning nedladdad"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>
              <Download className="h-4 w-4 mr-1.5" /> Ladda ner PDF
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Samlad vy där referenssvaren grupperas per karaktärsdimension (D1–D10), så mönster i dina styrkor syns direkt. Ladda ner en delbar PDF att bifoga din ansökan.</p>
          <div className="grid gap-4 md:grid-cols-2">
            {dimGroups.map((g) => (
              <div key={g.value} className="rounded-xl border border-border p-4" data-testid={`dim-group-${g.value}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">{g.label}</span>
                  <Badge variant="secondary" className="rounded-full">{g.answers.filter((a) => !a.cannot_assess).length} bekräftade</Badge>
                </div>
                <ul className="space-y-2">
                  {g.answers.map((a, i) => (
                    <li key={i} className="text-sm">
                      <span className="text-muted-foreground">{a.name}:</span>{" "}
                      {a.cannot_assess ? <span className="italic text-muted-foreground">Kunde ej bedöma</span> : <span>{a.text}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Card>
      )}

      <ListSection
        endpoint="references"
        fields={FIELDS}
        addLabel="Lägg till referens"
        emptyText="Inga referenser tillagda ännu."
        summary={(it) => ({ title: it.name, subtitle: it.relation, meta: `${it.ref_type || ""}${it.email ? " · " + it.email : ""}` })}
        extraAction={(item, reload) => (
          <InviteAction item={item} response={responses[item.id]} attachments={attByRef[item.id]} reload={() => { reload(); loadResponses(); }} />
        )}
      />
    </div>
  );
}
